import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import { STATUS_DEFINITIONS, getStatusStyle } from '../statusDefinitions';

// Offer pipeline: Created → Sent → Accepted (cumulative). Rejected shown as a
// separate counter-bar. FINISHED offers are folded into Accepted (no own stage).
// Headline: conversion rate (accepted / created).

// Offer status values used for color lookup. FINISHED was removed from
// STATUS_DEFINITIONS.offers, so we map the funnel keys to their status int here.
const STAGE_STATUS = { CREATED: 4, MAIL_SENT: 0, ACCEPTED: 1 };
const STAGE_LABEL = { CREATED: 'Created', MAIL_SENT: 'Sent', ACCEPTED: 'Accepted' };

const formatPct = (ratio) =>
  ratio === null || ratio === undefined ? '–' : `${Math.round(ratio * 100)}%`;

const FunnelBar = ({ count, max, color, label, colors, isRejected }) => {
  const widthPct = max > 0 ? (count / max) * 100 : 0;
  return (
    <Box className="flex items-center gap-2">
      <Typography
        variant="caption"
        sx={{
          color: colors.text,
          opacity: isRejected ? 0.6 : 0.85,
          fontSize: '0.72rem',
          width: 90,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={label}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 16,
          borderRadius: 4,
          backgroundColor: colors.bgInput,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${Math.max(widthPct, 2)}%`,
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.3s ease',
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: colors.text,
          fontWeight: 600,
          fontSize: '0.75rem',
          minWidth: 28,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};

const ConversionFunnel = ({ specials }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const stages = specials?.stages || [];
  const rejectedCount = specials?.rejectedCount ?? 0;
  const conversionRate = specials?.conversionRate;
  const winRate = specials?.winRate;

  const max = Math.max(...stages.map((s) => s.count ?? 0), rejectedCount, 1);
  const rejectedColor = getStatusStyle(colors, 'rejected').color;

  return (
    <Box className="flex flex-col gap-2">
      <Box className="flex items-center justify-between flex-wrap gap-2">
        <Typography
          variant="caption"
          sx={{ color: colors.text, opacity: 0.7, fontWeight: 500 }}
        >
          {t('Conversion funnel')}
        </Typography>
        {conversionRate !== null && conversionRate !== undefined && (
          <Box className="flex items-baseline gap-1">
            <Typography
              variant="body2"
              sx={{ color: colors.money?.positive, fontWeight: 700, fontSize: '0.95rem' }}
            >
              {formatPct(conversionRate)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.text, opacity: 0.5, fontSize: '0.68rem' }}
            >
              {t('Conversion rate')}
            </Typography>
          </Box>
        )}
      </Box>
      <Box className="flex flex-col gap-1">
        {stages.map((stage) => {
          const status = STAGE_STATUS[stage.key];
          const variant = STATUS_DEFINITIONS.offers[status]?.variant ?? 'open';
          const { color } = getStatusStyle(colors, variant);
          return (
            <FunnelBar
              key={stage.key}
              count={stage.count}
              max={max}
              color={color}
              label={t(STAGE_LABEL[stage.key] ?? stage.key)}
              colors={colors}
            />
          );
        })}
        {/* Rejected — separated, with win-rate hint */}
        <Box
          sx={{
            marginTop: 0.5,
            paddingTop: 0.75,
            borderTop: `1px dashed ${colors.selected}`,
          }}
        >
          <FunnelBar
            count={rejectedCount}
            max={max}
            color={rejectedColor}
            label={t('Rejected')}
            colors={colors}
            isRejected
          />
        </Box>
        {winRate !== null && winRate !== undefined && (
          <Typography
            variant="caption"
            sx={{ color: colors.text, opacity: 0.5, fontSize: '0.68rem', mt: 0.25, ml: '98px' }}
          >
            {t('Win rate')}: {formatPct(winRate)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ConversionFunnel;
